import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Paper,
  Skeleton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import StatusChip from '../components/StatusChip';
import WarningChips from '../components/WarningChips';
import ClaimDrawer from '../components/ClaimDrawer';
import { useTriageData } from './Triage/useTriageData';
import { billingApi } from '../services/billingApi';
import { usePractice } from '../contexts/PracticeContext';
import type { BillingSource, ClaimSummaryDto } from '../types/billing';

const VALID_SOURCES: BillingSource[] = ['elation', 'athena'];
const DEFAULT_SOURCE: BillingSource = 'elation';

function parseSource(raw: string | null): BillingSource {
  return raw && (VALID_SOURCES as string[]).includes(raw)
    ? (raw as BillingSource)
    : DEFAULT_SOURCE;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function formatCharges(amount: number | null): string {
  if (amount === null) return '-';
  return currencyFormatter.format(amount);
}

function formatPatient(claim: ClaimSummaryDto): string {
  const name = `${claim.patientFirstName ?? ''} ${claim.patientLastName ?? ''}`.trim();
  return name.length === 0 ? '-' : name;
}

interface SnackbarState {
  open: boolean;
  severity: 'success' | 'error';
  headline: string;
  detail?: string[];
}

const Triage: React.FC = () => {
  const { currentPracticeId } = usePractice();
  const [searchParams, setSearchParams] = useSearchParams();
  const source = parseSource(searchParams.get('source'));
  const { claims, isLoading, error, reload } = useTriageData(
    currentPracticeId ?? undefined,
    source,
  );

  const handleSourceChange = (_e: React.SyntheticEvent, newSource: BillingSource) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('source', newSource);
      return next;
    });
    setSelectedIds([]);
  };

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openClaimId, setOpenClaimId] = useState<string | null>(null);
  const [isDispatching, setIsDispatching] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    severity: 'success',
    headline: '',
  });

  const allSelected = claims.length > 0 && selectedIds.length === claims.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < claims.length;

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggleRow = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(claims.map((c) => c.id));
    }
  };

  const handleDispatch = async () => {
    if (!currentPracticeId || selectedIds.length === 0) return;
    setIsDispatching(true);
    try {
      const result = await billingApi.dispatch({
        practiceId: currentPracticeId,
        claimIds: selectedIds,
        force: false,
      });
      const dispatchedCount = result.dispatched.length;
      const skippedCount = result.skipped.length;
      const plural = dispatchedCount === 1 ? '' : 's';
      const skippedSuffix = skippedCount > 0 ? ` (${skippedCount} skipped)` : '';
      const headline = `Sent ${dispatchedCount} claim${plural} to BETHY${skippedSuffix}`;
      const detail =
        skippedCount > 0
          ? result.skipped.map((s) => `${s.claimId.slice(0, 8)}: ${s.reason}`)
          : undefined;
      setSnackbar({ open: true, severity: 'success', headline, detail });
      setSelectedIds([]);
      reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSnackbar({
        open: true,
        severity: 'error',
        headline: `Failed to send to BETHY: ${message}`,
      });
    } finally {
      setIsDispatching(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  return (
    <Box>
      <Tabs
        value={source}
        onChange={handleSourceChange}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        aria-label="claim source"
      >
        <Tab label="Elation" value="elation" />
        <Tab label="Athena" value="athena" />
      </Tabs>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5">Triage</Typography>
          <Typography variant="body2" color="text.secondary">
            {isLoading ? 'Loading…' : error !== null ? '-' : `${claims.length} ${source} claims`}
          </Typography>
        </Box>
        {!isLoading && error === null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedIds.length} of {claims.length} selected
            </Typography>
            <Button
              variant="contained"
              disabled={selectedIds.length === 0 || isDispatching}
              onClick={handleDispatch}
            >
              {isDispatching ? 'Sending to BETHY…' : 'Send to BETHY'}
            </Button>
          </Box>
        )}
      </Box>

      {isLoading && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Skeleton variant="rectangular" width={20} height={20} animation="wave" />
                </TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>DOS</TableCell>
                <TableCell>Carrier</TableCell>
                <TableCell>Lines</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Warnings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell padding="checkbox">
                    <Skeleton variant="rectangular" width={20} height={20} animation="wave" />
                  </TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {error !== null && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Could not load claims</AlertTitle>
          {error}
        </Alert>
      )}

      {!isLoading && error === null && claims.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <InboxOutlinedIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            No actionable claims
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Nothing currently needs triage. Anything in STAGED, FAILED,
            NEEDS_REVIEW, or SKIPPED would land here.
          </Typography>
        </Paper>
      )}

      {!isLoading && error === null && claims.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    inputProps={{ 'aria-label': 'select all claims' }}
                  />
                </TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>DOS</TableCell>
                <TableCell>Carrier</TableCell>
                <TableCell>Lines</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Warnings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {claims.map((row) => {
                const isSelected = selectedSet.has(row.id);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setOpenClaimId(row.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleRow(row.id)}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ 'aria-label': `select claim ${row.id}` }}
                      />
                    </TableCell>
                    <TableCell>{formatPatient(row)}</TableCell>
                    <TableCell>{row.dateOfService ?? '-'}</TableCell>
                    <TableCell>{row.primaryCarrier ?? '-'}</TableCell>
                    <TableCell>{row.lineCount ?? '-'}</TableCell>
                    <TableCell>{formatCharges(row.totalCharges)}</TableCell>
                    <TableCell>
                      <StatusChip status={row.status} />
                    </TableCell>
                    <TableCell>
                      <WarningChips codes={row.warnings} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ClaimDrawer claimId={openClaimId} onClose={() => setOpenClaimId(null)} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.headline}
          {snackbar.detail && snackbar.detail.length > 0 && (
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              {snackbar.detail.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Triage;
